/* ============================================================
   Dathent — Calendar
   Month + Week views over real scheduled_posts data. Slot click →
   /app/post/:id. URL state via ?view=&date=.
   Source: pages/calendar.jsx + Calendar.html.
   ============================================================ */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../services/api/client.js';
import { Icons as I } from '../components/pack-icons';
import { BrandIcons } from '../components/pack-icons/brands';
import { useGenerationStatus } from '../state/GenerationStatusContext.jsx';
import { useBrand } from '../contexts/BrandContext.jsx';
import { DeviceTimezoneLabel } from '../components/DeviceTimezoneLabel.jsx';
import TikTokStatisticsBanner from '../components/metrics/TikTokStatisticsBanner.jsx';
import { targetStatusToUi } from '../lib/postStatus.js';
import { platformDisplayName } from '../lib/platforms.js';

// Localized month / weekday names via Intl, so the calendar follows the UI
// language instead of being pinned to English. 2024-01-01 is a Monday, so the
// weekday helper returns Mon..Sun in order for the given locale.
const monthLong = (date, locale) => new Intl.DateTimeFormat(locale, { month: 'long' }).format(date);
const monthShort = (date, locale) => new Intl.DateTimeFormat(locale, { month: 'short' }).format(date);
const weekdayShortLabels = (locale) => {
  const fmt = new Intl.DateTimeFormat(locale, { weekday: 'short' });
  return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(Date.UTC(2024, 0, 1 + i))));
};

const PLATFORM_FROM_NAME = (raw) => {
  if (!raw) return null;
  const v = String(raw).toLowerCase();
  if (v.includes('insta')) return 'instagram';
  if (v.includes('face')) return 'facebook';
  if (v.includes('linke')) return 'linkedin';
  if (v === 'x' || v.includes('twit')) return 'x';
  if (v.includes('tik')) return 'tiktok';
  if (v.includes('thread')) return 'threads';
  if (v.includes('tele')) return 'telegram';
  return v;
};

const STATUS_TINT = {
  published: { dot: 'var(--success)', bg: 'color-mix(in srgb, var(--success) 14%, var(--bg-elev))' },
  scheduled: { dot: 'var(--accent)', bg: 'var(--bg-elev)' },
  queued: { dot: 'var(--accent)', bg: 'var(--bg-elev)' },
  live: { dot: 'var(--warning)', bg: 'color-mix(in srgb, var(--warning) 16%, var(--bg-elev))' },
  inbox: { dot: '#f59e0b', bg: 'color-mix(in srgb, #f59e0b 14%, var(--bg-elev))' },
  needs_check: { dot: '#f59e0b', bg: 'color-mix(in srgb, #f59e0b 14%, var(--bg-elev))' },
  draft: { dot: 'var(--text-subtle)', bg: 'var(--bg-subtle)' },
  failed: { dot: 'var(--danger)', bg: 'color-mix(in srgb, var(--danger) 14%, var(--bg-elev))' },
  // TASK-331 (M7 fix): neutral (same muted tint as 'draft') — paused pending
  // renewal is informational, not a failure or an in-progress state.
  // (round 4: the round-3 'missed' status was retired backend-side — a
  // past-dated post skipped while lapsed is now a plain 'failed' target, so
  // it renders with the ordinary 'failed' tint here, same as any other
  // failure. See screens/Scheduler.jsx for the one place that still
  // distinguishes it, via last_error_code.)
  blocked_billing: { dot: 'var(--text-subtle)', bg: 'var(--bg-subtle)' },
};

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function parseDateParam(value) {
  if (!value) return new Date();
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? new Date() : d;
}

function formatDateParam(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/* Strip "Generating: " / "Generating N/N: " prefix from prompt-style titles. */
function cleanPromptPrefix(s) {
  if (typeof s !== 'string') return s;
  return s.replace(/^Generating(?:\s+\d+\s*\/\s*\d+)?\s*:\s*/i, '').trim();
}

/* Resolve a human title for a scheduled post — scheduler payload nests
   content as { contentItem: {title, topic}, activeVersion: {hook, body, caption} }. */
function deriveSlotTitle(item) {
  const c = item?.content || {};
  const ci = c.contentItem || {};
  const v = c.activeVersion || {};
  const candidates = [
    v.hook,
    ci.title,
    v.body,
    v.caption,
    ci.topic,
  ];
  for (const raw of candidates) {
    if (typeof raw !== 'string') continue;
    const cleaned = cleanPromptPrefix(raw).split('\n')[0];
    if (cleaned && cleaned.length >= 3) return cleaned.slice(0, 80);
  }
  return 'Untitled post';
}

/* Map a server scheduled_post + targets into an array of slot rows.
 * Dedupes by (contentId, platform): when two scheduled_posts exist for
 * the same content_item × platform (typical after a clone-republish),
 * we keep only the slot with the best status so the calendar shows
 * current state, not a confusing "Failed Tuesday + Published Wednesday"
 * pair for the same post. Status preference: published > publishing >
 * scheduled > pending > failed.
 */
export function expandToSlots(items, brandById) {
  const statusRank = {
    published: 100,
    live: 90, publishing: 90,
    queued: 50, claimed: 50, scheduled: 50, pending: 50,
    needs_manual_action: 95, inbox: 95, needs_check: 95,
    failed: 10, needs_reconnect: 10, cancelled: 5,
    // TASK-331 (M7 fix): without an explicit rank this defaulted to 0 (see
    // `?? 0` below) — LOWER than even 'cancelled' — so a stale cancelled
    // duplicate for the same content+platform could hide a genuinely paused
    // blocked_billing entry in the dedupe above. Ranked just below the
    // hard-failure tier: worth surfacing over a routine scheduled/queued
    // slot, but a real failure or a needs-attention state still wins.
    // (round 4: the round-3 'missed' rank is gone — a missed-while-lapsed
    // target is now a plain 'failed' target and uses the 'failed' rank above.)
    blocked_billing: 20,
  };
  const bestPerKey = new Map(); // `${contentId}|${platform}` → slot
  const orphanSlots = [];

  for (const item of items) {
    const post = item?.post || item;
    const when = post?.scheduledAt || post?.scheduled_at;
    if (!when) continue;
    const d = new Date(when);
    if (Number.isNaN(d.getTime())) continue;
    const targets = item?.targets || [];
    const now = new Date();
    const live = Math.abs(d - now) < 60 * 60 * 1000;
    const baseStatus = post?.status || 'scheduled';
    const title = deriveSlotTitle(item);
    const brandProfileId = post?.brandProfileId || item?.brandProfileId || item?.content?.contentItem?.brandProfileId;
    const contentId = post.contentItemId || post.content_item_id || item?.content?.contentItem?.id || post.id;
    for (const t of (targets.length ? targets : [{}])) {
      const tStatus = t.status;
      const platform = PLATFORM_FROM_NAME(t.platform || post.platform || '');
      const manualStatus = tStatus === 'needs_manual_action'
        ? targetStatusToUi(tStatus, { platform, errorCode: t.lastErrorCode || t.last_error_code })
        : null;
      // TASK-331 (M7 fix): 'blocked_billing' (a lapsed org's paused target) is
      // deliberately NOT mapped to 'failed' or any other bucket here — it
      // falls through to the final `(tStatus || baseStatus)` branch, which
      // already passes it through as its own literal status. Named explicitly
      // so a future edit to this ternary doesn't accidentally reclassify it.
      // (round 4: the round-3 'missed' status is retired — a past-dated
      // target skipped while lapsed now arrives as a plain 'failed' target
      // and is caught by the `tStatus === 'failed'` branch below like any
      // other failure.)
      const status = tStatus === 'published'
        ? 'published'
        : manualStatus
          ? manualStatus
          : tStatus === 'failed' || tStatus === 'needs_reconnect'
            ? 'failed'
            : live && (tStatus === 'queued' || tStatus === 'claimed' || tStatus === 'pending')
              ? 'live'
              : tStatus === 'blocked_billing'
                ? tStatus
                : (tStatus || baseStatus);
      const slot = {
        id: `${post.id}::${t.id || 'main'}`,
        contentId,
        date: d,
        hour: d.getHours(),
        minute: d.getMinutes(),
        platform,
        title,
        status,
        brand: brandById.get(brandProfileId),
      };
      const dedupKey = contentId && platform ? `${contentId}|${platform}` : null;
      if (!dedupKey) {
        orphanSlots.push(slot);
        continue;
      }
      const newRank = statusRank[status] ?? statusRank[tStatus] ?? 0;
      const prev = bestPerKey.get(dedupKey);
      if (!prev || (statusRank[prev.status] ?? 0) < newRank) {
        bestPerKey.set(dedupKey, slot);
      } else if ((statusRank[prev.status] ?? 0) === newRank && slot.date > prev.date) {
        // Tie-break by most recent scheduledAt — keeps the latest entry.
        bestPerKey.set(dedupKey, slot);
      }
    }
  }
  return [...Array.from(bestPerKey.values()), ...orphanSlots];
}

/* Header summary. A 'pending' target is waiting for its time — that is how
   every scheduled publish_target arrives from the worker, so it counts as
   scheduled and can be the next post (GH-975). "Next post" is the earliest
   one not yet past — including one due within the hour, which expandToSlots
   shows as 'live' — so an overdue pending target never reads as next. */
const UPCOMING_SLOT_STATUSES = new Set(['scheduled', 'queued', 'pending']);

export function summarizeSlots(allSlots, now = new Date()) {
  const upcomingSlots = allSlots.filter((s) => UPCOMING_SLOT_STATUSES.has(s.status));
  return {
    total: allSlots.length,
    scheduled: upcomingSlots.length,
    published: allSlots.filter((s) => s.status === 'published').length,
    upcoming: allSlots
      .filter((s) => (UPCOMING_SLOT_STATUSES.has(s.status) || s.status === 'live') && s.date >= now)
      .sort((a, b) => a.date - b.date)[0],
  };
}

function BrandTile({ brand }) {
  if (!brand) return null;
  return (
    <span className="ca-brand-tile" title={brand.name}>
      {brand.logoUrl ? <img src={brand.logoUrl} alt="" /> : (brand.monogram || brand.name?.trim().charAt(0).toUpperCase())}
    </span>
  );
}

/* ============================================================
   Top bar — period stepper, view toggle, platform filter, new post
   ============================================================ */
function CalBar({ view, setView, cursor, setCursor, platformFilter, setPlatformFilter, onNewPost }) {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const label = view === 'month'
    ? `${monthLong(cursor, lng)} ${cursor.getFullYear()}`
    : (() => {
        const start = new Date(cursor);
        const dow = (start.getDay() + 6) % 7;
        start.setDate(start.getDate() - dow);
        const end = new Date(start);
        end.setDate(end.getDate() + 6);
        const sameMonth = start.getMonth() === end.getMonth();
        return sameMonth
          ? `${monthLong(start, lng)} ${start.getDate()}–${end.getDate()}, ${end.getFullYear()}`
          : `${monthShort(start, lng)} ${start.getDate()} – ${monthShort(end, lng)} ${end.getDate()}, ${end.getFullYear()}`;
      })();

  const step = (dir) => {
    const next = new Date(cursor);
    if (view === 'month') next.setMonth(next.getMonth() + dir);
    else next.setDate(next.getDate() + 7 * dir);
    setCursor(next);
  };

  const platforms = ['all', 'instagram', 'facebook', 'linkedin', 'x', 'tiktok', 'threads', 'telegram'];

  return (
    <div className="ca-bar">
      <div className="ca-bar-l">
        <button className="btn btn-ghost btn-sm" onClick={() => step(-1)} aria-label={t('calendar.previous')} type="button">
          <I.chevLeft size={14} />
        </button>
        <h2 className="ca-period">{label}</h2>
        <button className="btn btn-ghost btn-sm" onClick={() => step(1)} aria-label={t('calendar.next')} type="button">
          <I.chevRight size={14} />
        </button>
        <button className="btn btn-ghost btn-sm" onClick={() => setCursor(new Date())} type="button">
          {t('calendar.today')}
        </button>
      </div>

      <div className="ca-bar-r">
        <div className="ca-plat-filter">
          {platforms.map((p) => (
            <button
              key={p}
              className={`lib-plat-chip ${platformFilter === p ? 'is-active' : ''}`}
              onClick={() => setPlatformFilter(p)}
              title={p === 'all' ? t('calendar.allPlatforms') : platformDisplayName(p)}
              type="button"
            >
              {p === 'all'
                ? <span style={{ fontSize: 10, padding: '0 4px' }}>{t('calendar.all')}</span>
                : <span
                    className={`ob-net-ic-${p}`}
                    style={{
                      width: 18, height: 18, borderRadius: 4,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
                    }}
                  >
                    {BrandIcons[p]?.({ size: 11 })}
                  </span>}
            </button>
          ))}
        </div>
        <div className="ca-view">
          <button className={view === 'week' ? 'is-active' : ''} onClick={() => setView('week')} type="button">
            {t('calendar.week')}
          </button>
          <button className={view === 'month' ? 'is-active' : ''} onClick={() => setView('month')} type="button">
            {t('calendar.month')}
          </button>
        </div>
        <button className="btn btn-primary btn-sm" onClick={onNewPost} type="button">
          <I.plus size={13} /> {t('calendar.newPost')}
        </button>
      </div>
    </div>
  );
}

/* ============================================================
   Slot pill (clickable, navigates to PostPreview)
   ============================================================ */
function SlotPill({ slot, compact, onClick }) {
  const { t } = useTranslation();
  const tint = STATUS_TINT[slot.status] || STATUS_TINT.scheduled;
  const time = `${String(slot.hour).padStart(2, '0')}:${String(slot.minute).padStart(2, '0')}`;
  const attentionLabel = slot.status === 'inbox'
    ? t('postPreview.inboxDraftTitle')
    : slot.status === 'needs_check' ? t('postPreview.statusNeedsCheck') : null;
  return (
    <button
      type="button"
      onClick={() => onClick(slot)}
      className="ca-slot"
      style={{ background: tint.bg }}
      title={attentionLabel ? `${attentionLabel} · ${slot.title} · ${time}` : `${slot.title} · ${time}`}
    >
      {slot.platform && (
        <span className={`ca-slot-plat ob-net-ic-${slot.platform}`}>
          {BrandIcons[slot.platform]?.({ size: 10 })}
        </span>
      )}
      <span className="ca-slot-time mono">{time}</span>
      {!compact && <span className="ca-slot-title">{slot.title}</span>}
      <span className="ca-slot-dot" style={{ background: tint.dot }} />
    </button>
  );
}

/* ============================================================
   Day inspector — slides in from the right with the day's full post list
   ============================================================ */
function DayInspector({ day, slots, onClose, onOpenPost, onCreate }) {
  const { t, i18n } = useTranslation();

  // ESC to close. MUST stay above the `if (!day) return null` guard below:
  // hooks have to run in the same order on every render, and registering this
  // effect only on the day-set render (i.e. after the early return) meant the
  // hook count jumped 1→2 the moment the inspector opened — that was the React
  // #310 ("rendered more hooks than during the previous render") crash on
  // opening a day from the calendar. The effect no-ops while closed.
  useEffect(() => {
    if (!day) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [day, onClose]);

  if (!day) return null;
  const sorted = [...slots].sort((a, b) => a.hour * 60 + a.minute - (b.hour * 60 + b.minute));
  // Grouped by label ('pending' and 'scheduled' are both "Scheduled").
  const statusCounts = sorted.reduce((m, s) => {
    const label = calendarStatusLabel(s.status, t);
    m[label] = m[label] || { status: s.status, count: 0 };
    m[label].count += 1;
    return m;
  }, {});

  return (
    <>
      <div className="ca-insp-scrim" onClick={onClose} />
      <aside className="ca-insp" role="dialog" aria-label={t('calendar.postsForSelectedDay')}>
        <header className="ca-insp-h">
          <div>
            <div className="eyebrow"><span>{t('calendar.selectedDay')}</span></div>
            <h3>{day.toLocaleDateString(i18n.language, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</h3>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn btn-soft btn-sm" onClick={() => onCreate(day)} type="button">
              <I.plus size={12} /> {t('calendar.new')}
            </button>
            <button className="btn btn-ghost btn-sm btn-icon" onClick={onClose} type="button" aria-label={t('calendar.close')}>
              <I.x size={14} />
            </button>
          </div>
        </header>

        <div className="ca-insp-counts">
          <div className="ca-insp-count-card">
            <strong>{sorted.length}</strong>
            <span>{sorted.length === 1 ? t('calendar.post') : t('calendar.posts')}</span>
          </div>
          <div className="ca-insp-counts-pills">
            {Object.entries(statusCounts).map(([label, { status, count }]) => {
              const tint = STATUS_TINT[status] || STATUS_TINT.scheduled;
              return (
                <span
                  key={label}
                  className="pill"
                  style={{ background: tint.bg, color: tint.dot, borderColor: 'transparent' }}
                >
                  <span className="dot" style={{ background: tint.dot }} />
                  {label} · {count}
                </span>
              );
            })}
          </div>
        </div>

        <div className="ca-insp-list">
          {sorted.length === 0 ? (
            <div className="ca-insp-empty">
              <I.calendar size={20} />
              <span>{t('calendar.dayOpenHint')}</span>
            </div>
          ) : (
            sorted.map((s) => {
              const tint = STATUS_TINT[s.status] || STATUS_TINT.scheduled;
              const time = `${String(s.hour).padStart(2, '0')}:${String(s.minute).padStart(2, '0')}`;
              return (
                <button
                  key={s.id}
                  className="ca-insp-post"
                  type="button"
                  onClick={() => onOpenPost(s)}
                  style={{ borderLeftColor: tint.dot }}
                >
                  <div className="ca-insp-post-h">
                    <span className="ca-insp-time mono">{time}</span>
                    {s.platform && (
                      <span
                        className={`ob-net-ic-${s.platform}`}
                        style={{
                          width: 22, height: 22, borderRadius: 5,
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
                        }}
                      >
                        {BrandIcons[s.platform]?.({ size: 12 })}
                      </span>
                    )}
                    <span className="pill" style={{ background: tint.bg, color: tint.dot, borderColor: 'transparent', marginLeft: 'auto' }}>
                      <span className="dot" style={{ background: tint.dot }} />
                      {calendarStatusLabel(s.status, t)}
                    </span>
                  </div>
                  <div className="ca-insp-title"><BrandTile brand={s.brand} />{s.title}</div>
                </button>
              );
            })
          )}
        </div>
      </aside>
    </>
  );
}

/* Distribute the day's slot statuses into proportional bar segments. */
function dayStatusBars(slots) {
  if (!slots.length) return [];
  const groups = {};
  for (const s of slots) {
    groups[s.status] = (groups[s.status] || 0) + 1;
  }
  return Object.entries(groups).map(([status, count]) => ({
    status,
    weight: count / slots.length,
  }));
}

/* "3 published · 2 scheduled" type summary. Grouped by label, so a
   'pending' target and a 'scheduled' post read as one "Scheduled". */
function dayStatusSummary(slots, t) {
  if (!slots.length) return '';
  const groups = {};
  for (const s of slots) {
    const label = calendarStatusLabel(s.status, t);
    groups[label] = (groups[label] || 0) + 1;
  }
  return Object.entries(groups)
    .sort(([, a], [, b]) => b - a)
    .map(([label, count]) => `${count} ${label}`)
    .join(' · ');
}

const STATUS_LABEL_SHORT = {
  published: 'Published',
  scheduled: 'Scheduled',
  // GH-975: the worker's status for a target waiting for its time — the
  // raw 'pending' string used to leak into the chip.
  pending: 'Scheduled',
  queued: 'Queued',
  live: 'Publishing',
  draft: 'Draft',
  failed: 'Failed',
  // TASK-331 (M7 fix): plain-English, matching every sibling entry above
  // (this map is never localized via t() — only the 'inbox' special-case in
  // calendarStatusLabel below is).
  blocked_billing: 'Paused (plan lapsed)',
  // (round 4: the round-3 'missed' entry is gone — a missed-while-lapsed
  // post now reads as a plain 'Failed' target here, same as any failure.)
};

export function calendarStatusLabel(status, t) {
  if (status === 'inbox') return t('postPreview.inboxDraftTitle');
  if (status === 'needs_check') return t('postPreview.statusNeedsCheck');
  return STATUS_LABEL_SHORT[status] || status;
}

/* ============================================================
   Month view
   ============================================================ */
function MonthView({ cursor, slots, onDayClick, selectedDay }) {
  const { t, i18n } = useTranslation();
  const today = startOfDay(new Date());
  const weekDays = weekdayShortLabels(i18n.language);
  const firstDay = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const lastDay = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
  const startWeekday = (firstDay.getDay() + 6) % 7;
  const totalCells = Math.ceil((startWeekday + lastDay.getDate()) / 7) * 7;

  const slotsByDay = useMemo(() => {
    const m = new Map();
    for (const s of slots) {
      const key = startOfDay(s.date).toDateString();
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(s);
    }
    for (const arr of m.values()) {
      arr.sort((a, b) => a.hour * 60 + a.minute - (b.hour * 60 + b.minute));
    }
    return m;
  }, [slots]);

  const cells = [];
  for (let i = 0; i < totalCells; i++) {
    const dayOffset = i - startWeekday;
    const date = new Date(firstDay);
    date.setDate(1 + dayOffset);
    const inMonth = date.getMonth() === cursor.getMonth();
    const isToday = date.toDateString() === today.toDateString();
    const dayPosts = slotsByDay.get(startOfDay(date).toDateString()) || [];
    cells.push({ date, inMonth, isToday, posts: dayPosts });
  }

  return (
    <div className="ca-month">
      <div className="ca-month-h">
        {weekDays.map((d, idx) => <div key={idx} className="ca-month-h-d">{d}</div>)}
      </div>
      <div className="ca-month-grid">
        {cells.map((c, i) => {
          const isSelected = selectedDay && c.date.toDateString() === selectedDay.toDateString();
          const hasPosts = c.posts.length > 0;
          const next = c.posts[0] || null;
          const nextTint = next ? (STATUS_TINT[next.status] || STATUS_TINT.scheduled) : null;
          const bars = dayStatusBars(c.posts);
          const summary = dayStatusSummary(c.posts, t);
          return (
            <div
              key={i}
              className={`ca-day ${!c.inMonth ? 'is-out' : ''} ${c.isToday ? 'is-today' : ''} ${isSelected ? 'is-selected' : ''} ${c.inMonth ? 'is-clickable' : ''}`}
              onClick={() => { if (c.inMonth) onDayClick(c.date); }}
              role={c.inMonth ? 'button' : undefined}
              tabIndex={c.inMonth ? 0 : -1}
              aria-label={`${c.date.toDateString()}${hasPosts ? t('calendar.dayPostsCount', { count: c.posts.length }) : t('calendar.dayNoPosts')}`}
              onKeyDown={(e) => {
                if (c.inMonth && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  onDayClick(c.date);
                }
              }}
            >
              <div className="ca-day-h">
                <span className="ca-day-num">{c.date.getDate()}</span>
                {hasPosts && <span className="ca-day-count">{c.posts.length}</span>}
              </div>

              {hasPosts ? (
                <div className="ca-day-overview">
                  {/* Status progress bar */}
                  <div className="ca-day-bar" aria-hidden="true">
                    {bars.map((b) => {
                      const tint = STATUS_TINT[b.status] || STATUS_TINT.scheduled;
                      return (
                        <span
                          key={b.status}
                          style={{ flexGrow: b.weight, background: tint.dot }}
                        />
                      );
                    })}
                  </div>

                  <div className="ca-day-brief">
                    <span className="ca-day-total">
                      {c.posts.length} {c.posts.length === 1 ? t('calendar.post') : t('calendar.posts')}
                    </span>
                    <span className="ca-day-mix">{summary}</span>
                  </div>

                  {next && (
                    <div
                      className="ca-day-next"
                      style={{ borderLeftColor: nextTint.dot, background: nextTint.bg }}
                    >
                      <div className="ca-day-next-h">
                        <span className="ca-day-next-time mono">
                          {String(next.hour).padStart(2, '0')}:{String(next.minute).padStart(2, '0')}
                        </span>
                        {next.platform && (
                          <span
                            className={`ob-net-ic-${next.platform}`}
                            style={{
                              width: 14, height: 14, borderRadius: 3,
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
                            }}
                          >
                            {BrandIcons[next.platform]?.({ size: 8 })}
                          </span>
                        )}
                        <span className="ca-day-next-status" style={{ color: nextTint.dot }}>
                          {calendarStatusLabel(next.status, t)}
                        </span>
                      </div>
                      <div className="ca-day-next-title"><BrandTile brand={next.brand} />{next.title}</div>
                      {c.posts.length > 1 && (
                        <div className="ca-day-more">{t('calendar.moreCount', { count: c.posts.length - 1 })}</div>
                      )}
                    </div>
                  )}
                </div>
              ) : c.inMonth ? (
                <div className="ca-day-empty-state">
                  <span className="ca-day-empty-label">{t('calendar.noPosts')}</span>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ============================================================
   Week view
   ============================================================ */
function WeekView({ cursor, slots, onSlotClick }) {
  const { i18n } = useTranslation();
  const today = startOfDay(new Date());
  const start = new Date(cursor);
  const dow = (start.getDay() + 6) % 7;
  start.setDate(start.getDate() - dow);
  start.setHours(0, 0, 0, 0);
  const days = Array.from({ length: 7 }).map((_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
  const hours = [6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21];

  const slotsByDay = useMemo(() => {
    const m = new Map();
    for (const s of slots) {
      const key = startOfDay(s.date).toDateString();
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(s);
    }
    return m;
  }, [slots]);

  return (
    <div className="ca-week">
      <div className="ca-week-h">
        <div className="ca-week-h-spacer" />
        {days.map((d, i) => {
          const isToday = d.toDateString() === today.toDateString();
          return (
            <div key={i} className={`ca-week-h-d ${isToday ? 'is-today' : ''}`}>
              <span className="ca-week-h-day">{d.toLocaleDateString(i18n.language, { weekday: 'short' })}</span>
              <span className="ca-week-h-num">{d.getDate()}</span>
            </div>
          );
        })}
      </div>
      <div className="ca-week-body">
        <div className="ca-week-hours">
          {hours.map((h) => (
            <div key={h} className="ca-week-hour">
              <span className="ca-week-hour-l mono">{String(h).padStart(2, '0')}:00</span>
            </div>
          ))}
        </div>
        {days.map((d, di) => {
          const dayPosts = slotsByDay.get(d.toDateString()) || [];
          return (
            <div key={di} className="ca-week-col">
              {hours.map((h) => <div key={h} className="ca-week-cell" />)}
              {dayPosts.map((p) => {
                const top = ((p.hour - 6) + p.minute / 60) * 56; // 56px per hour
                if (top < 0) return null; // skip pre-6am
                const tint = STATUS_TINT[p.status] || STATUS_TINT.scheduled;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => onSlotClick(p)}
                    className={`ca-wk-slot ca-wk-${p.status}`}
                    style={{ top, background: tint.bg, borderLeftColor: tint.dot }}
                  >
                    <div className="ca-wk-slot-h">
                      {p.platform && (
                        <span
                          className={`ca-slot-plat ob-net-ic-${p.platform}`}
                          style={{ width: 16, height: 16 }}
                        >
                          {BrandIcons[p.platform]?.({ size: 10 })}
                        </span>
                      )}
                      <span className="mono" style={{ fontSize: 10, color: 'var(--text-subtle)' }}>
                        {String(p.hour).padStart(2, '0')}:{String(p.minute).padStart(2, '0')}
                      </span>
                    </div>
                    <div className="ca-wk-slot-t"><BrandTile brand={p.brand} />{p.title}</div>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ============================================================
   Top-level Calendar screen
   ============================================================ */
export const Calendar = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const initialView = searchParams.get('view') === 'week' ? 'week' : 'month';
  const initialDate = parseDateParam(searchParams.get('date'));

  const [view, setView] = useState(initialView);
  const [cursor, setCursor] = useState(initialDate);
  const [platformFilter, setPlatformFilter] = useState('all');
  const [calendarItems, setCalendarItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const { enabled: brandsEnabled, brands } = useBrand();
  const brandById = useMemo(
    () => brandsEnabled && brands.length > 1 ? new Map(brands.map((brand) => [brand.id, brand])) : new Map(),
    [brandsEnabled, brands]
  );
  // Watch publish operations — when one terminates, refetch calendar so
  // a freshly-published post replaces its old "failed" slot without the
  // user waiting for the 30s GET cache to expire.
  const { operationsArr } = useGenerationStatus();
  const publishOpTerminalSig = useMemo(() => {
    return operationsArr
      .filter((op) => op.kind === 'publish' && (op.status === 'succeeded' || op.status === 'failed'))
      .map((op) => `${op.id}:${op.status}:${op.updatedAt}`)
      .sort()
      .join('|');
  }, [operationsArr]);

  // Persist view + date in URL
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set('view', view);
    next.set('date', formatDateParam(cursor));
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, cursor]);

  // Fetch calendar window when cursor / view changes
  useEffect(() => {
    let cancelled = false;
    let from, to;
    if (view === 'month') {
      from = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
      to = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    } else {
      const start = new Date(cursor);
      const dow = (start.getDay() + 6) % 7;
      start.setDate(start.getDate() - dow);
      start.setHours(0, 0, 0, 0);
      from = start;
      to = new Date(start);
      to.setDate(start.getDate() + 7);
    }
    setLoading(true);
    // Bust the GET cache before each fetch — otherwise a publish-op
    // signal change (retry succeeded) would serve the stale snapshot.
    try { api.invalidateCache?.('/api/scheduler/calendar'); } catch (_) {}
    api.scheduler.getCalendar(from.toISOString(), to.toISOString())
      .then((res) => { if (!cancelled) setCalendarItems(res?.posts || res?.data?.posts || []); })
      .catch(() => { if (!cancelled) setCalendarItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [view, cursor, publishOpTerminalSig]);

  const allSlots = useMemo(() => expandToSlots(calendarItems, brandById), [calendarItems, brandById]);
  const slots = useMemo(() => {
    if (platformFilter === 'all') return allSlots;
    return allSlots.filter((s) => s.platform === platformFilter);
  }, [allSlots, platformFilter]);

  const summary = useMemo(() => summarizeSlots(allSlots), [allSlots]);

  const handleSlotClick = (slot) => {
    // Carry the target/platform context so PostPreview renders the right
    // platform chrome even when one content_item targets multiple networks.
    const params = new URLSearchParams();
    if (slot.targetId) params.set('target', slot.targetId);
    if (slot.platform) params.set('platform', slot.platform);
    const qs = params.toString();
    navigate(`/app/post/${encodeURIComponent(slot.contentId)}${qs ? `?${qs}` : ''}`);
  };
  const handleNewPost = () => navigate('/app/create');
  const handleDayClick = (day) => setSelectedDay(day);

  // Slots for the currently-selected day (filtered by active platform)
  const selectedDaySlots = useMemo(() => {
    if (!selectedDay) return [];
    return slots.filter((s) => startOfDay(s.date).toDateString() === selectedDay.toDateString());
  }, [selectedDay, slots]);

  return (
    <div className="ca-root">
      <TikTokStatisticsBanner />
      <div className="ca-top">
        <div>
          <h1 className="ca-h">{t('calendar.title')}</h1>
          <p className="ca-sub">{t('calendar.subtitle')} · <DeviceTimezoneLabel /></p>
        </div>
        <div className="ca-summary">
          <div className="ca-sum">
            <span className="ca-sum-l">{t('calendar.scheduled')}</span>
            <span className="ca-sum-v">{summary.scheduled}</span>
          </div>
          <div className="ca-sum">
            <span className="ca-sum-l">{t('calendar.publishedThisWindow')}</span>
            <span className="ca-sum-v">{summary.published}</span>
          </div>
          {summary.upcoming && (
            <div className="ca-sum">
              <span className="ca-sum-l">{t('calendar.nextPost')}</span>
              <span className="ca-sum-v" style={{ fontSize: 13, fontWeight: 500 }}>
                {monthShort(summary.upcoming.date, i18n.language)} {summary.upcoming.date.getDate()} · {String(summary.upcoming.hour).padStart(2, '0')}:{String(summary.upcoming.minute).padStart(2, '0')}
              </span>
            </div>
          )}
        </div>
      </div>

      <CalBar
        view={view}
        setView={setView}
        cursor={cursor}
        setCursor={setCursor}
        platformFilter={platformFilter}
        setPlatformFilter={setPlatformFilter}
        onNewPost={handleNewPost}
      />

      {loading && allSlots.length === 0 ? (
        <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-subtle)' }}>{t('calendar.loading')}</div>
      ) : view === 'month' ? (
        <MonthView
          cursor={cursor}
          slots={slots}
          onDayClick={handleDayClick}
          selectedDay={selectedDay}
        />
      ) : (
        <WeekView cursor={cursor} slots={slots} onSlotClick={handleSlotClick} />
      )}

      <DayInspector
        day={selectedDay}
        slots={selectedDaySlots}
        onClose={() => setSelectedDay(null)}
        onOpenPost={(s) => {
          handleSlotClick(s);
          setSelectedDay(null);
        }}
        onCreate={(d) => {
          setSelectedDay(null);
          navigate(`/app/create?date=${formatDateParam(d)}`);
        }}
      />

      <div className="ca-legend">
        <span className="ca-legend-l">{t('calendar.status')}</span>
        <span className="ca-legend-i"><i style={{ background: 'var(--accent)' }} />{t('calendar.scheduled')}</span>
        <span className="ca-legend-i"><i style={{ background: 'var(--warning)' }} />{t('calendar.publishingToday')}</span>
        <span className="ca-legend-i"><i style={{ background: 'var(--success)' }} />{t('calendar.published')}</span>
        <span className="ca-legend-i"><i style={{ background: 'var(--danger)' }} />{t('calendar.failed')}</span>
      </div>
    </div>
  );
};

export default Calendar;
